import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Download, TrendingUp, MessageSquare, Clock, Users, Send, CheckCheck, Eye, XCircle, Calendar } from 'lucide-react';
import axios from 'axios';
import api from '../services/api';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const COLORS = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899'];

function Reports() {
  const [data, setData] = useState({
    dashboard: {},
    responseTime: [],
    funnel: [],
    volumeBySector: [],
    dailyVolume: [],
    weekdayPerformance: []
  });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7);

  // NOVO: métricas de campanhas (envios por setor/número, por dia/mês, por campanha)
  const [campaignsData, setCampaignsData] = useState({
    bySector: [],
    byPeriod: [],
    list: []
  });
  const [campaignsGroupBy, setCampaignsGroupBy] = useState('day'); // 'day' | 'month'
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);

  useEffect(() => {
    loadData();
  }, [period]);

  useEffect(() => {
    loadCampaignsData();
  }, [campaignsGroupBy]);

  const loadData = async () => {
    try {
      setLoading(true);
            const [
        dashboardRes,
        responseTimeRes,
        funnelRes,
        volumeBySectorRes,
        dailyVolumeRes,
        weekdayRes
      ] = await Promise.all([
        api.get('/api/reports/dashboard'),
        api.get('/api/reports/response-time'),
        api.get('/api/reports/funnel-conversion'),
        api.get('/api/reports/volume-by-sector'),
        api.get(`/api/reports/daily-volume?days=${period}`),
        api.get('/api/reports/weekday-performance')
      ]);

      setData({
        dashboard: dashboardRes.data,
        responseTime: responseTimeRes.data,
        funnel: funnelRes.data,
        volumeBySector: volumeBySectorRes.data,
        dailyVolume: dailyVolumeRes.data,
        weekdayPerformance: weekdayRes.data
      });
    } catch (err) {
      console.error('Erro ao carregar relatórios:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignsData = async () => {
    try {
      setLoadingCampaigns(true);
      const [bySectorRes, byPeriodRes, listRes] = await Promise.all([
        api.get('/api/reports/campaigns-by-sector'),
        api.get(`/api/reports/campaigns-by-period?group_by=${campaignsGroupBy}`),
        api.get('/api/reports/campaigns-list')
      ]);
      setCampaignsData({
        bySector: bySectorRes.data,
        byPeriod: byPeriodRes.data,
        list: listRes.data
      });
    } catch (err) {
      console.error('Erro ao carregar métricas de campanhas:', err);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // NOVO: totais de hoje e do mês atual, derivados do volume por período
  const todayStr = new Date().toISOString().slice(0, 10);
  const monthStr = new Date().toISOString().slice(0, 7);
  const sentToday = campaignsGroupBy === 'day'
    ? (campaignsData.byPeriod.find(p => p.period === todayStr)?.total_sent || 0)
    : null;
  const sentThisMonth = campaignsData.list
    .filter(c => (c.created_at || '').slice(0, 7) === monthStr)
    .reduce((sum, c) => sum + (parseInt(c.sent_count) || 0), 0);
  const totalSentAllTime = campaignsData.list.reduce((sum, c) => sum + (parseInt(c.sent_count) || 0), 0);
  const totalDeliveredAllTime = campaignsData.list.reduce((sum, c) => sum + (parseInt(c.delivered_count) || 0), 0);
  const totalReadAllTime = campaignsData.list.reduce((sum, c) => sum + (parseInt(c.read_count) || 0), 0);

  const exportReport = () => {
    const reportData = {
      generated_at: new Date().toISOString(),
      period_days: period,
      ...data
    };
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relatórios e Métricas</h1>
          <p className="text-gray-500">Métricas precisas para tomada de decisão baseada em dados</p>
        </div>
        <div className="flex gap-3">
          <select value={period} onChange={e => setPeriod(parseInt(e.target.value))}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none">
            <option value={7}>Últimos 7 dias</option>
            <option value={15}>Últimos 15 dias</option>
            <option value={30}>Últimos 30 dias</option>
          </select>
          <button onClick={exportReport} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700">
            <Download size={18} /> Exportar
          </button>
        </div>
      </div>

      {/* Métricas Principais */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <MessageSquare size={20} />
            </div>
            <span className="text-sm text-gray-500">Total Conversas</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{data.dashboard.total_conversations || 0}</p>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-50 text-green-600 rounded-xl">
              <TrendingUp size={20} />
            </div>
            <span className="text-sm text-gray-500">Mensagens</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{data.dashboard.total_messages || 0}</p>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-orange-50 text-orange-600 rounded-xl">
              <Clock size={20} />
            </div>
            <span className="text-sm text-gray-500">Tempo Médio Resposta</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{data.dashboard.avg_response_time_minutes || 0} min</p>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-xl">
              <Users size={20} />
            </div>
            <span className="text-sm text-gray-500">Conversas Ativas</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{data.dashboard.active_conversations || 0}</p>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Desempenho por Dia da Semana */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Desempenho de Atendimento</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.weekdayPerformance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day_name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="message_count" fill="#10B981" name="Mensagens" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Volume Diário */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Volume de Mensagens (Últimos {period} dias)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.dailyVolume}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="message_date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="message_count" stroke="#10B981" name="Total" />
              <Line type="monotone" dataKey="inbound_count" stroke="#3B82F6" name="Recebidas" />
              <Line type="monotone" dataKey="outbound_count" stroke="#8B5CF6" name="Enviadas" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Conversão do Funil */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Taxa de Conversão do Funil</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.funnel} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="stage_name" type="category" width={120} />
              <Tooltip />
              <Legend />
              <Bar dataKey="conversations_count" fill="#3B82F6" name="Conversas" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Volume por Setor */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Volume por Setor</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.volumeBySector}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ sector_name, total_conversations }) => `${sector_name}: ${total_conversations}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="total_conversations"
              >
                {data.volumeBySector.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* NOVO: Métricas de Campanhas */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Send size={22} className="text-green-600" />
            Métricas de Campanhas
          </h2>
        </div>

        {/* Cards de totais */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-green-50 text-green-600 rounded-xl"><Send size={20} /></div>
              <span className="text-sm text-gray-500">Total Enviados</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalSentAllTime}</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-50 text-blue-600 rounded-xl"><CheckCheck size={20} /></div>
              <span className="text-sm text-gray-500">Entregues</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalDeliveredAllTime}</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-purple-50 text-purple-600 rounded-xl"><Eye size={20} /></div>
              <span className="text-sm text-gray-500">Lidos</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalReadAllTime}</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-orange-50 text-orange-600 rounded-xl"><Calendar size={20} /></div>
              <span className="text-sm text-gray-500">Enviados Este Mês</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{sentThisMonth}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Volume de envios por dia/mês */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Envios ao Longo do Tempo</h3>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setCampaignsGroupBy('day')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${campaignsGroupBy === 'day' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                >
                  Por dia
                </button>
                <button
                  onClick={() => setCampaignsGroupBy('month')}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${campaignsGroupBy === 'month' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                >
                  Por mês
                </button>
              </div>
            </div>
            {loadingCampaigns ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={campaignsData.byPeriod}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total_sent" stroke="#10B981" name="Enviados" />
                  <Line type="monotone" dataKey="delivered_count" stroke="#3B82F6" name="Entregues" />
                  <Line type="monotone" dataKey="read_count" stroke="#8B5CF6" name="Lidos" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Volume por setor/número */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Envios por Setor / Número</h3>
            {loadingCampaigns ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={campaignsData.bySector}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="sector_name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="total_sent" fill="#10B981" name="Enviados" />
                  <Bar dataKey="delivered_count" fill="#3B82F6" name="Entregues" />
                  <Bar dataKey="read_count" fill="#8B5CF6" name="Lidos" />
                </BarChart>
              </ResponsiveContainer>
            )}
            {!loadingCampaigns && campaignsData.bySector.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {campaignsData.bySector.map((s, idx) => (
                  <span key={idx} className="text-xs px-2.5 py-1 bg-gray-50 text-gray-600 rounded-full">
                    {s.sector_name}{s.display_phone_number ? ` · ${s.display_phone_number}` : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tabela por campanha: entregues, lidos, falhas */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Detalhamento por Campanha</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 font-semibold">Campanha</th>
                  <th className="px-4 py-3 font-semibold">Setor / Número</th>
                  <th className="px-4 py-3 font-semibold">Data</th>
                  <th className="px-4 py-3 font-semibold">Enviados</th>
                  <th className="px-4 py-3 font-semibold">Entregues</th>
                  <th className="px-4 py-3 font-semibold">Lidos</th>
                  <th className="px-4 py-3 font-semibold">Falhas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {campaignsData.list.map((c) => {
                  const sent = parseInt(c.sent_count) || 0;
                  const delivered = parseInt(c.delivered_count) || 0;
                  const read = parseInt(c.read_count) || 0;
                  const failed = parseInt(c.failed_count) || 0;
                  const deliveredPct = sent > 0 ? Math.round((delivered / sent) * 100) : 0;
                  const readPct = sent > 0 ? Math.round((read / sent) * 100) : 0;
                  return (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{c.name}</p>
                        {c.template_name && <p className="text-xs text-gray-400">{c.template_name}</p>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm">
                        {c.sector_name || '—'}
                        {c.display_phone_number && <span className="block text-xs text-gray-400">{c.display_phone_number}</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('pt-BR') : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-900 font-medium">{sent}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                          {delivered} ({deliveredPct}%)
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                          {read} ({readPct}%)
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {failed > 0 ? (
                          <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                            <XCircle size={12} /> {failed}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!loadingCampaigns && campaignsData.list.length === 0 && (
                  <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-400">Nenhuma campanha encontrada</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Tempo de Resposta por Atendente */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Tempo de Resposta por Atendente</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-6 py-3 font-semibold">Atendente</th>
                <th className="px-6 py-3 font-semibold">Setor</th>
                <th className="px-6 py-3 font-semibold">Conversas</th>
                <th className="px-6 py-3 font-semibold">Tempo Médio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.responseTime.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">{item.user_name || 'Não atribuído'}</td>
                  <td className="px-6 py-4 text-gray-600">{item.sector_name || '—'}</td>
                  <td className="px-6 py-4 text-gray-600">{item.total_conversations}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                      {item.avg_first_response_formatted}
                    </span>
                  </td>
                </tr>
              ))}
              {data.responseTime.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-8 text-center text-gray-400">Nenhum dado disponível</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Reports;
